<template>
  <div :class="allClasses">
    <ENotificationBanner
      v-if="featureEnabled('notifications')"
      :background-m="bannerBackgroundM"
      :background-l="bannerBackgroundL"
    />
    <header class="main-header__content" aria-label="Main header">
      <div class="main-header__navigation">
        <div v-if="featureEnabled('logo')" class="main-header__logo">
          <ELogo
            :href="logoUrl"
            :padding="false"
            class="main-header__logo-link"
            data-test-id="main-header-logo"
          />
        </div>
        <div v-if="searchEnabled" class="main-header__search" data-test-id="main-header-search">
          <ESearchForm
            :aria-label="$t('common.search.searchbox.placeholder.desktop')"
            :placeholder="$t('common.search.searchbox.placeholder.desktop')"
            :market="bingMarketCode"
            :action="searchUrl"
            :autocomplete-url="autocompleteUrl"
            prefill-query
            variant="compact"
            data-test-id="main-header-search-form"
          />
        </div>
        <transition name="fade-cta">
          <div v-if="visibleCta" class="main-header__install-cta">
            <ECta
              :show-share="showShareCta"
              :custom-cta-text="ctaText"
              size="m"
              data-track-id="serp_install_cta"
              data-test-id="main-header-install-cta"
            />
          </div>
        </transition>
        <div v-if="featureEnabled('tree-counter')" class="main-header__tree-counter">
          <EPersonalCounter v-if="personalCounterFetched" />
          <div
            v-else
            class="main-header__counter-placeholder"
          />
        </div>
        <div v-if="featureEnabled('notifications')" class="main-header__notifications">
          <ENotifications
            :blog-url="blogUrl"
            :read-more-label="$t('common.header.notifications.readmore')"
          />
        </div>
        <div class="main-header__nav">
          <EMainNav />
        </div>
      </div>
      <div v-if="$slots.navigation" class="main-header__search-navigation">
        <slot name="navigation" />
      </div>
    </header>
    <slot name="filters" />
  </div>
</template>

<script>
// TODO: translate aria-label on .main-header__content
import { mapState } from 'vuex';

import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';
import { BLOCKED_BROWSERS as blockedBrowsers } from '~~/common/js/constants/browsers';
import { HEADER_INSTALL_CTA_DISMISSED } from '~~/common/js/constants/cookies';
import { ECOSIA_APP_BROWSERS as ecosiaAppBrowsers } from '~~/common/js/constants/ecosia-apps';
import { getBlogURL } from '~~/common/js/universal/menus';
import classes from '~~/common/vue/mixins/classes';
import loginMixin from '~~/common/vue/mixins/login';

import BannerBackgroundM from '~~/common/vue/assets/images/banner-background-mobile.png';
import BannerBackgroundL from '~~/common/vue/assets/images/banner-background.png';
import ELogo from '~~/common/vue/components/logo';
import ECta from '~~/common/vue/components/cta';
import EMainNav from '~~/common/vue/components/main-nav';
import ENotifications from '~~/common/vue/components/notifications';
import ENotificationBanner from '~~/common/vue/components/notifications/banner';
import ESearchForm from '~~/common/vue/components/search-form';
import EPersonalCounter from '~~/common/vue/components/personal-counter';

export const headerFeatures = [
  'logo',
  'search',
  'notifications',
  'tree-counter',
  'install-cta',
  'check-tree-count'
];
// To disable search feature in future for ios, add 'ecosia ios'
export const disableSearchBrowsers = [];

// eslint-disable-next-line arrow-body-style
const headerFeatureValidator = (features) => {
  return features.every((feature) => headerFeatures.includes(feature));
};

export const INSTALL_CTA_MIN_TREE_COUNT = 3;

export default {
  name: 'EMainHeader',
  components: {
    ELogo,
    ECta,
    EMainNav,
    ENotifications,
    ENotificationBanner,
    ESearchForm,
    EPersonalCounter,
  },
  mixins: [classes, loginMixin],
  props: {
    /**
     * A list of features which will be disabled. All enabled by default.
     * One of `logo`, `search`, `notifications`, `tree-counter`, `install-cta`.
     */
    disableFeatures: {
      type: Array,
      default: () => [],
      validator: headerFeatureValidator,
    },
    border: {
      type: Boolean,
      default: true,
      cssClass: true,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    showShareCta: {
      type: Boolean,
      default: false,
    },
    ctaText: {
      type: String,
      default: '',
    },
    searchAutocomplete: {
      type: Boolean,
      default: true,
    },
    logoUrl: {
      type: String,
      default: '/'
    }
  },
  data: () => ({
    shouldShowCta: false,
    bannerBackgroundM: BannerBackgroundM,
    bannerBackgroundL: BannerBackgroundL,
  }),
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', ['bingMarketCode', 'browser', 'treeCount']),
    searchUrl() {
      if (process.env.searchUrl) {
        return process.env.searchUrl;
      }
      switch (this.$route.path) {
        case '/':
        case '/settings':
          return '/search';
        default:
          return this.$route.path;
      }
    },
    autocompleteUrl() {
      return this.searchAutocomplete && process.env.autocompleteUrl ? process.env.autocompleteUrl : '';
    },
    allClasses() {
      const allClasses = [...this.classes];
      if (!this.$slots.searchNavigation) {
        allClasses.push('main-header--with-verticals');
      }
      if (this.isSticky) {
        allClasses.push('main-header--sticky');
      }
      return allClasses;
    },
    blogUrl() {
      return getBlogURL(this.locale);
    },
    searchEnabled() {
      return disableSearchBrowsers.includes(this.browser) ?
        false :
        this.featureEnabled('search');
    },
    visibleCta() {
      return this.featureEnabled('install-cta') && this.shouldShowCta;
    },
  },
  watch: {
    filtersShown(value) {
      if (value) {
        // defer to next event loop cycle, because Vue won't have
        // rendered the filters at this point. `$nextTick` didn't
        // seem to work in this case.
        setTimeout(() => {
          const filters = document.getElementById('search-filters');
          if (filters) {
            focusFocusableChild(filters);
          }
        }, 0);
      }
    },
  },
  mounted() {
    const treeCountConditionIsMet = this.featureEnabled('check-tree-count') ? this.treeCount >= INSTALL_CTA_MIN_TREE_COUNT : true;
    this.shouldShowCta = treeCountConditionIsMet &&
      !blockedBrowsers.includes(this.browser) &&
      !ecosiaAppBrowsers.includes(this.browser) && // TODO: do this in ecosia-user
      !this.$cookies.get(HEADER_INSTALL_CTA_DISMISSED);

    if (this.featureEnabled('tree-counter')) {
      this.initHeimdallLogin();
    }
  },
  methods: {
    featureEnabled(feature) {
      return !this.disableFeatures.includes(feature);
    },
  },
};
</script>

<style lang="scss" scoped>
.main-header__logo {
  align-self: center;
  margin-left: auto;
  order: 2;

  @include tablet {
    align-self: auto;
  }

  @include desktop {
    margin-left: 0;
    order: unset;
  }
}

.main-header__logo-link {
  height: 50px;
  width: 70px;
  margin: $space-s 0;
  position: relative;

  @include desktop {
    margin: $space-s $space-1l $space-s $space-1s;
  }
}

.main-header__search {
  order: 4; // after tree counter, TBA
  width: 100%;

  @include desktop {
    flex-shrink: 1;
    max-width: $page-container-width-desktop;
    order: unset;

    .main-header__logo + & {
      margin: 0 $space-l 0 0;
    }
  }
}

.main-header__nav {
  order: 1;
}
// bypassing stylelint here because the block it's complaining
// about is only a placeholder and will be removed
// stylelint-disable-next-line no-duplicate-selectors
.main-header__notifications {
  display: none;
  flex-shrink: 0;

  @include desktop {
    display: block;
    margin-right: $space-s;
    order: unset;
  }
}

.main-header__tree-counter,
.main-header__counter-placeholder {
  flex-shrink: 0;
  margin-left: auto;
  margin-right: 0;
  order: 3;

  @include desktop {
    margin-right: $space-s;
    order: unset;
  }
}

.main-header__counter-placeholder {
  width: 45px;
}

.main-header__install-cta {
  display: none;

  @include desktop {
    display: block;
    margin: 0 $space-2l 0 $space-l;
  }
}

.main-header__content {
  background: $color-background-primary;
  padding: 0 $space-s $space-s;
  position: relative;
  z-index: $z-index-1l;

  & > * {
    flex-shrink: 0;
  }

  @include tablet {
    padding: 0 $space-s $space-m;
  }

  @include desktop {
    padding: 0 $space-l;
  }
}

.main-header--with-verticals .main-header__content {
  padding-bottom: 0;
}

.main-header--sticky {
  position: sticky;
  top: 0;
  z-index: $z-index-1l;
}

.main-header--border .main-header__content {
  @include elevation(1);

  @include desktop {
    @include elevation(0);
    @include divider('bottom');
  }
}

.main-header__navigation,
.main-header__search-navigation {
  width: 100%;
}

.main-header__navigation {
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  @include desktop-down {
    // Applies when navbar is not used, e.g. Settings
    &:last-child {
      padding-bottom: $space-s;
    }
  }

  @include desktop {
    flex-wrap: nowrap;
  }
}

.main-header__search-navigation {
  @include desktop {
    padding-left: $page-container-left-space-desktop - $space-l;
  }
}

.fade-cta-leave-active {
  opacity: 0;
  transition: opacity $timing-l;
}
</style>
